import { Link, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { User, LogOut, BookOpen, Menu as MenuIcon, X, Coins, Gift, Megaphone } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { userApi } from '../../api/client';

export function Header() {
  const { user, isAuthenticated, logout, updateUser } = useAuthStore();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Fetch fresh user profile to keep credits in sync
  const { data: profileData } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => userApi.getProfile(),
    enabled: isAuthenticated,
    refetchInterval: 30000, // Refresh every 30 seconds
    refetchOnMount: 'always',
  });

  // Update auth store when profile data changes
  useEffect(() => {
    if (profileData?.data && profileData.data.credit_balance !== user?.credit_balance) {
      updateUser({ credit_balance: profileData.data.credit_balance });
    }
  }, [profileData?.data, user?.credit_balance, updateUser]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex items-center space-x-2">
              <img src="/logo.png" alt="Review Like a Pro" className="w-8 h-8" />
              <span className="text-xl font-bold text-gray-900 hidden sm:inline">Review Like a Pro</span>
            </Link>

            {isAuthenticated && (
              <nav className="hidden md:flex space-x-6">
                <Link
                  to="/tasks"
                  className="text-gray-600 hover:text-primary-600 transition-colors"
                >
                  Tasks
                </Link>
                <Link
                  to="/my-tasks"
                  className="text-gray-600 hover:text-primary-600 transition-colors"
                >
                  My Tasks
                </Link>
                <Link
                  to="/credits"
                  className="text-gray-600 hover:text-primary-600 transition-colors"
                >
                  Credits
                </Link>
                <Link
                  to="/referral"
                  className="text-gray-600 hover:text-primary-600 transition-colors"
                >
                  Referral
                </Link>
                <Link
                  to="/announcements"
                  className="text-gray-600 hover:text-primary-600 transition-colors"
                >
                  Announcements
                </Link>
                <Link
                  to="/guide"
                  className="text-gray-600 hover:text-primary-600 transition-colors"
                >
                  User Guide
                </Link>
                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="text-gray-600 hover:text-primary-600 transition-colors font-medium"
                  >
                    Admin
                  </Link>
                )}
              </nav>
            )}
          </div>

          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            {isAuthenticated && (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <MenuIcon className="w-6 h-6" />
                )}
              </button>
            )}

            {isAuthenticated ? (
              <>
                <div className="hidden sm:flex items-center space-x-2 px-3 py-1.5 bg-primary-50 rounded-lg">
                  <Coins className="w-4 h-4 text-primary-600" />
                  <span className="text-sm font-medium text-primary-700">
                    {user?.credit_balance || 0} credits
                  </span>
                </div>

                <Menu as="div" className="relative">
                  <Menu.Button className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {user?.full_name?.[0]?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  </Menu.Button>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 mt-2 w-56 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                      <div className="p-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>

                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/profile"
                              className={`${
                                active ? 'bg-gray-50' : ''
                              } flex items-center px-4 py-2 text-sm text-gray-700`}
                            >
                              <User className="w-4 h-4 mr-3" />
                              Profile
                            </Link>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/credits"
                              className={`${
                                active ? 'bg-gray-50' : ''
                              } flex items-center px-4 py-2 text-sm text-gray-700`}
                            >
                              <Coins className="w-4 h-4 mr-3" />
                              Credit History
                            </Link>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/referral"
                              className={`${
                                active ? 'bg-gray-50' : ''
                              } flex items-center px-4 py-2 text-sm text-gray-700`}
                            >
                              <Gift className="w-4 h-4 mr-3" />
                              Referral Program
                            </Link>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/announcements"
                              className={`${
                                active ? 'bg-gray-50' : ''
                              } flex items-center px-4 py-2 text-sm text-gray-700`}
                            >
                              <Megaphone className="w-4 h-4 mr-3" />
                              Announcements
                            </Link>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/guide"
                              className={`${
                                active ? 'bg-gray-50' : ''
                              } flex items-center px-4 py-2 text-sm text-gray-700`}
                            >
                              <BookOpen className="w-4 h-4 mr-3" />
                              User Guide
                            </Link>
                          )}
                        </Menu.Item>
                      </div>

                      <div className="border-t border-gray-100 py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleLogout}
                              className={`${
                                active ? 'bg-gray-50' : ''
                              } flex items-center w-full px-4 py-2 text-sm text-red-600`}
                            >
                              <LogOut className="w-4 h-4 mr-3" />
                              Sign out
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </>
            ) : (
              <Link
                to="/login"
                className="btn-primary px-4 py-2 text-sm rounded-lg"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isAuthenticated && (
        <Transition
          show={isMobileMenuOpen}
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 -translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 -translate-y-1"
        >
          <nav className="md:hidden border-t border-gray-200 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 space-y-1">
              <Link
                to="/tasks"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-primary-600 transition-colors"
              >
                Tasks
              </Link>
              <Link
                to="/my-tasks"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-primary-600 transition-colors"
              >
                My Tasks
              </Link>
              <Link
                to="/credits"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-primary-600 transition-colors"
              >
                Credits
              </Link>
              <Link
                to="/referral"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-primary-600 transition-colors"
              >
                Referral
              </Link>
              <Link
                to="/announcements"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-primary-600 transition-colors"
              >
                Announcements
              </Link>
              <Link
                to="/guide"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-primary-600 transition-colors"
              >
                User Guide
              </Link>
              {user?.role === 'admin' && (
                <Link
                  to="/admin"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-primary-600 font-medium transition-colors"
                >
                  Admin
                </Link>
              )}
            </div>
          </nav>
        </Transition>
      )}
    </header>
  );
}
